import { planDisplayName } from 'src/constants/planDisplay'
import { formatParLevelsHabitProgress } from './growthEngineHabitProgress'

export const FREE_PAR_LEVEL_LIMIT = 10

export const PAR_LEVEL_LIMIT_ERROR_CODE = 'PAR_LEVEL_LIMIT'

export const PAR_LEVEL_LIMIT_MESSAGE = `You've set Par Levels on ${FREE_PAR_LEVEL_LIMIT} products. Upgrade to Starter to manage Par Levels across your full inventory.`

/**
 * Benefit-led headline for the dashboard paywall (Phase C). PAR_LEVEL_LIMIT_MESSAGE above
 * mirrors the API error string and stays as-is; the modal leads with this instead.
 */
export const PAR_LEVEL_PAYWALL_HEADLINE = `Par levels tell OMS what to order back up to. ${planDisplayName(
  'Free',
)} includes them on ${FREE_PAR_LEVEL_LIMIT} products — Starter covers your full inventory.`

export const PAR_LEVEL_PAYWALL_WORKAROUND_MESSAGE =
  'Starter, Growth, and Pro set par levels on every product. Or remove a par level from another product to free a slot on OMS Lite.'

/** @deprecated Use PAR_LEVEL_PAYWALL_WORKAROUND_MESSAGE */
export const PAR_LEVEL_PAYWALL_HELP_MESSAGE =
  PAR_LEVEL_PAYWALL_WORKAROUND_MESSAGE

export const PAR_LEVEL_PAYWALL_STARTER_BENEFITS = [
  'Unlimited par levels across your full inventory',
  'User roles — control who can change recipes, GP, and settings',
  'Unlimited restricted suppliers',
  'More recipes and invoice uploads',
]

export const PAR_LEVEL_PAYWALL_HIGHER_TIERS_LINE =
  'Growth and Pro unlock stock take, Xero, and more.'

export const PAR_LEVEL_PAYWALL_TRUST_LINE =
  'Starter from £29/month. Cancel anytime.'

export const getParLevelPaywallAppMessage = () => {
  const bullets = PAR_LEVEL_PAYWALL_STARTER_BENEFITS.slice(0, 3)
    .map(benefit => `• ${benefit}`)
    .join('\n')

  return [
    PAR_LEVEL_PAYWALL_HEADLINE,
    '',
    PAR_LEVEL_PAYWALL_WORKAROUND_MESSAGE,
    '',
    'Starter also includes:',
    bullets,
    '',
    PAR_LEVEL_PAYWALL_HIGHER_TIERS_LINE,
    '',
    PAR_LEVEL_PAYWALL_TRUST_LINE,
  ].join('\n')
}

export const PAR_LEVEL_UPGRADE_PLAN = 'Starter'

/** Starter, Growth, and Pro have unlimited par levels; only Free is capped. */
export const isParLevelPlanUnlimited = plan => plan !== 'Free'

export const MAX_PAR_LEVEL = 999

const toParLevel = value => {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  if (n > MAX_PAR_LEVEL) return MAX_PAR_LEVEL
  return n
}

export const getParLevelForProduct = product => toParLevel(product?.parLevel)

/** Distinct venue products with parLevel > 0 in the current shopping list. */
export const countVenueProductsWithParLevel = (venueProducts = []) =>
  venueProducts.filter(product => getParLevelForProduct(product) > 0).length

/**
 * Whether a save should be blocked by the Free-plan par level cap.
 * Mirrors server/src/utils/parLevelPaywall.js.
 */
export const wouldBlockParLevelSave = ({
  newParLevel,
  currentParLevel = 0,
  plan,
  parLevelProductCount,
}) => {
  if (isParLevelPlanUnlimited(plan)) {
    return false
  }

  const next = toParLevel(newParLevel)
  const current = toParLevel(currentParLevel)

  if (next <= 0) {
    return false
  }

  if (current > 0) {
    return false
  }

  return parLevelProductCount >= FREE_PAR_LEVEL_LIMIT
}

/**
 * Client-side preview before calling the venue product save API.
 * Uses the in-memory shopping list plus the product being edited.
 */
export const previewParLevelSave = ({
  venueProducts = [],
  venueProductId = null,
  newParLevel,
  plan = 'Free',
}) => {
  const parLevelProductCount = countVenueProductsWithParLevel(venueProducts)
  const existing = venueProductId
    ? venueProducts.find(product => product._id === venueProductId)
    : null
  const currentParLevel = existing ? getParLevelForProduct(existing) : 0

  const blocked = wouldBlockParLevelSave({
    newParLevel,
    currentParLevel,
    plan,
    parLevelProductCount,
  })

  return {
    blocked,
    plan,
    parLevelProductCount,
    currentParLevel,
    limit: FREE_PAR_LEVEL_LIMIT,
    isUnlimited: isParLevelPlanUnlimited(plan),
    errorCode: blocked ? PAR_LEVEL_LIMIT_ERROR_CODE : null,
    message: blocked ? PAR_LEVEL_LIMIT_MESSAGE : null,
    upgradePlan: blocked ? PAR_LEVEL_UPGRADE_PLAN : null,
  }
}

/** Free-plan header copy. Paid is unlimited — no cap line, no upgrade tone. */
export const formatParLevelUsage = (parLevelProductCount, plan) => {
  if (isParLevelPlanUnlimited(plan)) {
    return null
  }

  return formatParLevelsHabitProgress(
    parLevelProductCount,
    FREE_PAR_LEVEL_LIMIT,
  )
}

/** Par column header. Free-only cap line. */
export const stockTakeParColumnHelp = (plan = 'Free') => {
  const base = 'Target to order back to. Saved with this stock take.'
  if (isParLevelPlanUnlimited(plan)) return base
  return `${base} ${planDisplayName(
    plan,
  )} includes ${FREE_PAR_LEVEL_LIMIT} par levels.`
}

/** Par input tooltip. Free-only cap line. */
export const stockTakeParInputHelp = (plan = 'Free') => {
  const base = 'Set or edit par. Saved with Save changes.'
  if (isParLevelPlanUnlimited(plan)) return base
  return `${base} ${planDisplayName(
    plan,
  )} includes par levels on ${FREE_PAR_LEVEL_LIMIT} products.`
}

export const isParLevelLimitError = error => {
  const code = error?.response?.data?.code ?? error?.code
  return code === PAR_LEVEL_LIMIT_ERROR_CODE
}

export const normalizeParLevelInput = value => {
  let text = String(value ?? '').replace(/[^0-9.]/g, '')
  const firstDot = text.indexOf('.')
  if (firstDot !== -1) {
    text =
      text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, '')
  }

  if (text === '' || text === '.') {
    return '0'
  }

  const parsed = Number(text)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return '0'
  }

  if (parsed > MAX_PAR_LEVEL) {
    return String(MAX_PAR_LEVEL)
  }

  return text
}

export const parseParLevelValue = value => toParLevel(value)

export const isValidParLevelValue = value => {
  if (value === '' || value == null) {
    return true
  }

  const n = Number(value)
  return Number.isFinite(n) && n >= 0 && n <= MAX_PAR_LEVEL
}

export const coerceParLevelInput = value => {
  const text = normalizeParLevelInput(value)
  if (text.endsWith('.')) {
    return text
  }

  return parseParLevelValue(text)
}
